/**
 * Demo session store — Zustand.
 * Persists SST submissions and account-update changes during a demo session.
 * Resets on full page reload (acceptable demo-ware behavior per brief).
 * DDD: this is runtime state, not fixture data. Fixtures seed; store mutates.
 */
import { create } from "zustand";
import type { ServiceRequest } from "./service-requests";
import { generateOutageReportRefNumber } from "./service-requests";

const EMPTY_REQUESTS: ServiceRequest[] = [];
const EMPTY_PAYMENTS: PaymentEntry[] = [];

/**
 * `YYYY-MM-DD` in LOCAL time.
 *
 * Never `toISOString().slice(0, 10)` here: that is UTC, so any action taken
 * after ~20:00 in TCI (UTC-4/-5) records tomorrow's date and the receipt
 * contradicts the clock on the customer's wall. Same defect class as N-1, where
 * the outage list dated events a day ahead of the detail panel.
 */
function todayIsoLocal(now: Date = new Date()): string {
  const y = String(now.getFullYear());
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * One payment the customer made this session.
 *
 * A running sum could never answer "when did I pay, and what was my
 * confirmation number?", so payment history and Recent Activity could not show
 * a payment the customer had just made. Records can.
 */
export interface PaymentEntry {
  /** Amount paid, USD. */
  amount: number;
  /** `YYYY-MM-DD`, local time — the day the customer actually paid. */
  dateIso: string;
  /** The confirmation number the flow displayed. Ties the row to the receipt. */
  confirmation: string;
}

export interface AccountUpdateData {
  name: string;
  email: string;
  phone: string;
  address: string;
  eBilling: boolean;
  prefs: { email: boolean; sms: boolean; push: boolean };
}

export interface FeedbackEntry {
  rating: number;
  text: string;
  personaId: string;
}

export interface VacationModeData {
  active: boolean;
  from: string;
  to: string;
}

interface DemoSessionState {
  /** Per-persona service request submissions */
  submittedRequests: Record<string, ServiceRequest[]>;
  addRequest: (personaId: string, req: ServiceRequest) => void;
  getSubmittedRequests: (personaId: string) => ServiceRequest[];

  /** Clears all per-persona session state. Active persona untouched. */
  resetSession: () => void;

  submitOutageReport: (params: {
    personaId: string;
    address: string;
    district: string;
  }) => ServiceRequest;

  /** Per-persona account updates */
  accountUpdates: Record<string, AccountUpdateData>;
  updateAccount: (personaId: string, data: AccountUpdateData) => void;
  getAccountUpdate: (personaId: string) => AccountUpdateData | undefined;

  /** Feedback submissions */
  feedback: FeedbackEntry[];
  addFeedback: (entry: FeedbackEntry) => void;

  /** Per-persona vacation mode */
  vacationModes: Record<string, VacationModeData>;
  saveVacationMode: (personaId: string, data: VacationModeData) => void;
  getVacationMode: (personaId: string) => VacationModeData | undefined;

  /** Per-persona payment ledger, newest first. The SSoT for what has been paid. */
  paymentEntries: Record<string, PaymentEntry[]>;
  recordPayment: (personaId: string, amount: number, confirmation: string) => void;
  /** Derived from the ledger — the public shape callers already depend on. */
  getTotalPaid: (personaId: string) => number;
  /** The persona's payments, newest first. Stable reference when empty. */
  getPaymentEntries: (personaId: string) => PaymentEntry[];
}

export const useDemoSessionStore = create<DemoSessionState>((set, get) => ({
  submittedRequests: {},
  addRequest: (personaId, req) => {
    set((s) => ({
      submittedRequests: {
        ...s.submittedRequests,
        [personaId]: [...(s.submittedRequests[personaId] ?? []), req],
      },
    }));
  },
  getSubmittedRequests: (personaId) => get().submittedRequests[personaId] ?? EMPTY_REQUESTS,

  submitOutageReport: ({ personaId, address, district }) => {
    const refNumber = generateOutageReportRefNumber();
    // Local, not UTC — see `todayIsoLocal`. A report filed at 21:00 in TCI was
    // being stamped with tomorrow's date (same class as N-1).
    const now = todayIsoLocal();
    const req: ServiceRequest = {
      id: refNumber,
      type: "Outage Report",
      status: {
        state: "in-progress",
        receivedAt: now,
        expectedCompletion: now,
      },
      summary: `Power outage reported at ${address}, ${district}`,
    };
    set((s) => ({
      submittedRequests: {
        ...s.submittedRequests,
        [personaId]: [...(s.submittedRequests[personaId] ?? []), req],
      },
    }));
    return req;
  },

  accountUpdates: {},
  updateAccount: (personaId, data) => {
    set((s) => ({
      accountUpdates: { ...s.accountUpdates, [personaId]: data },
    }));
  },
  getAccountUpdate: (personaId) => get().accountUpdates[personaId],

  feedback: [],
  addFeedback: (entry) => {
    set((s) => ({ feedback: [...s.feedback, entry] }));
  },

  vacationModes: {},
  saveVacationMode: (personaId, data) => {
    set((s) => ({
      vacationModes: { ...s.vacationModes, [personaId]: data },
    }));
  },
  getVacationMode: (personaId) => get().vacationModes[personaId],

  paymentEntries: {},
  recordPayment: (personaId, amount, confirmation) => {
    const entry: PaymentEntry = { amount, dateIso: todayIsoLocal(), confirmation };
    set((s) => ({
      paymentEntries: {
        ...s.paymentEntries,
        // Prepend: the ledger is stored in the order every surface renders it.
        [personaId]: [entry, ...(s.paymentEntries[personaId] ?? [])],
      },
    }));
  },
  getTotalPaid: (personaId) =>
    (get().paymentEntries[personaId] ?? EMPTY_PAYMENTS).reduce((sum, p) => sum + p.amount, 0),
  getPaymentEntries: (personaId) => get().paymentEntries[personaId] ?? EMPTY_PAYMENTS,

  resetSession: () => {
    set({
      submittedRequests: {},
      accountUpdates: {},
      feedback: [],
      vacationModes: {},
      paymentEntries: {},
    });
  },
}));
